import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  List,
  Paper,
  Snackbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Professional, Team } from "@syadem/ariane-js";
import { VaccinationAct, VaccinationActCertificator } from "@syadem/kairos-citizen-js";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { DownloadCertificateButton } from "../../components/DownloadCertificateButton";
import { VaccinationActListSort } from "../../components/VaccinationActListSort";
import VaccinationActsByDiseaseGroups from "../../components/VaccinationActsByDiseaseGroups";
import VaccinationActsByList from "../../components/VaccinationActsByList";
import { IconMedicalInstrumentSyringe } from "../../components/icons/IconMedicalInstrumentSyringe";
import { EmptyListPlaceholder } from "../../components/shared/EmptyListPlaceholder";
import { VaccinationActForm } from "../../components/vaccination/VaccinationActForm";
import { EvcImportDialog } from "../../components/evc/EvcImportDialog";
import { useArianeApi } from "../../hooks/useArianeApi";
import { useDaphne } from "../../hooks/useDaphne";
import { useHealthRecord } from "../../hooks/useHealthRecord";
import { useI18n } from "../../hooks/useI18n";
import { theme } from "../../layout/Theme";

export function VaccinationActs() {
  const { id } = useParams() as { id: string };
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const [snackOpen, setSnackOpen] = useState<boolean>(searchParams.get("updated") == "true");
  const daphne = useDaphne();
  const arianeApi = useArianeApi();
  const { healthRecord, isLoading, error, refetch } = useHealthRecord(id);
  const vaccinationActs = healthRecord?.vaccinationActs;
  const [sortedVaccinationActs, setSortedVaccinationActs] = useState<VaccinationAct[]>([]);
  const [certificators, setCertificators] = useState<(Professional | Team)[] | undefined>(undefined);
  const [listType, setListType] = useState("byDiseases");
  const [drawerFormOpen, setDrawerFormOpen] = useState(false);
  const [openDatamatrix, setOpenDatamatrix] = useState(false);
  const [evcDialogOpen, setEvcDialogOpen] = useState(false);
  const closeDrawer = () => {
    setDrawerFormOpen(false);
    setOpenDatamatrix(false);
  };
  const breakpointSm = useMediaQuery(theme.breakpoints.down("md"));
  const drawerWidth = breakpointSm ? "100%" : "50%";
  const enableEvcImport = import.meta.env.VITE_ENABLE_EVC_IMPORT === "true";

  useEffect(() => {
    let isCancelled = false;
    if (!isCancelled && sortedVaccinationActs.length === 0 && vaccinationActs && vaccinationActs.length > 0) {
      setSortedVaccinationActs(vaccinationActs);
    }
    return () => {
      isCancelled = true;
    };
  }, [sortedVaccinationActs, vaccinationActs]);

  useEffect(() => {
    if (vaccinationActs) {
      const certificatorIds = vaccinationActs
        .filter((act) => act.certificator !== null)
        .map((act) => (act.certificator as VaccinationActCertificator).id);

      if (certificatorIds.length > 0) {
        arianeApi.professionals
          .searchByIds([...new Set(certificatorIds)])
          .then((actCertificators) => {
            setCertificators(actCertificators);
          })
          .catch((_error) => {
            setCertificators([]);
          });
      }
    }
  }, [arianeApi?.professionals, vaccinationActs]);

  return (
    <>
      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={() => {
          setSnackOpen(false);
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ ml: "150px" }}
      >
        <Alert severity="success">{t("common.alerts.alert_saved_success")}</Alert>
      </Snackbar>

      <Paper sx={{ overflow: "hidden", backgroundColor: "inherit" }} elevation={0}>
        {error && (
          <Typography sx={{ my: 4 }} align="center" component="div">
            <Alert severity="error">{t("common.alerts.alert_notification")}</Alert>
          </Typography>
        )}
        {isLoading && <LinearProgress variant="query" sx={{ my: 2 }} />}
        {!isLoading && healthRecord && daphne && vaccinationActs && vaccinationActs.length > 0 && (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                mt: 1,
              }}
            >
              <Box
                sx={{
                  width: { xs: "100%", md: "auto" },
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: "center",
                  mt: { xs: 1, md: 0 },
                  gap: 1,
                }}
              >
                <VaccinationActListSort
                  vaccinationActs={vaccinationActs}
                  setSortedVaccinationActs={setSortedVaccinationActs}
                  daphne={daphne}
                  setListType={setListType}
                />
                <DownloadCertificateButton healthRecord={healthRecord} />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  size={breakpointSm ? "small" : "medium"}
                  onClick={() => setDrawerFormOpen(true)}
                  startIcon={<AddIcon />}
                  disableElevation
                  variant="contained"
                >
                  {t("vaccines.add_vaccination")}
                </Button>
                {enableEvcImport && (
                  <Button variant="outlined" onClick={() => setEvcDialogOpen(true)} startIcon={<QrCodeScannerIcon />}>
                    {t("vaccines.scan_evc")}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  onClick={() => {
                    setDrawerFormOpen(true);
                    setOpenDatamatrix(true);
                  }}
                  startIcon={<SearchIcon />}
                >
                  Datamatrix
                </Button>
              </Box>
            </Box>
            <Typography variant="body2" color="rgba(0, 0, 0, 0.6)">
              {t("vaccines.count", {
                smart_count: sortedVaccinationActs?.length,
              })}
            </Typography>

            <List sx={{ width: "100%" }}>
              {listType === "byList" ? (
                <VaccinationActsByList
                  daphne={daphne}
                  certificators={certificators || []}
                  healthRecord={healthRecord}
                  sortedVaccinationActs={sortedVaccinationActs}
                />
              ) : listType === "byDiseases" ? (
                <VaccinationActsByDiseaseGroups
                  vaccinationActs={vaccinationActs}
                  daphne={daphne}
                  certificators={certificators}
                  healthRecord={healthRecord}
                />
              ) : null}
            </List>
          </>
        )}
        <Drawer
          anchor="right"
          open={drawerFormOpen}
          onClose={closeDrawer}
          sx={{ width: drawerWidth, "& .MuiDrawer-paper": { width: drawerWidth } }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            <IconButton onClick={closeDrawer} sx={{ ml: 2 }}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
          <Divider />
          <Paper sx={{ border: "none", px: 4, py: 2 }} elevation={0}>
            {healthRecord ? (
              <VaccinationActForm
                isEmbedded
                healthRecord={healthRecord}
                refreshCallback={refetch}
                setSnackOpen={setSnackOpen}
                handleClose={closeDrawer}
                isDatamatrixOpen={openDatamatrix}
              />
            ) : (
              <LinearProgress variant="query" />
            )}
          </Paper>
        </Drawer>
        {!isLoading && (!vaccinationActs || (vaccinationActs && vaccinationActs.length === 0)) ? (
          <EmptyListPlaceholder
            title={t("vaccines.no_vaccinations")}
            icon={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: theme.palette.primary[500],
                  ".iconSyringe": {
                    height: "35px !important",
                  },
                }}
              >
                <IconMedicalInstrumentSyringe />
              </Box>
            }
            button={
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  size="large"
                  onClick={() => setDrawerFormOpen(true)}
                  variant="contained"
                  startIcon={<AddIcon />}
                  disableElevation
                >
                  {t("vaccines.add_vaccination")}
                </Button>
                {enableEvcImport && (
                  <Button
                    size="large"
                    variant="outlined"
                    onClick={() => setEvcDialogOpen(true)}
                    startIcon={<QrCodeScannerIcon />}
                  >
                    {t("vaccines.scan_evc")}
                  </Button>
                )}
              </Box>
            }
          >
            {t("vaccines.add_vaccination_msg")}
          </EmptyListPlaceholder>
        ) : null}
        {enableEvcImport && (
          <EvcImportDialog
            open={evcDialogOpen}
            onClose={() => setEvcDialogOpen(false)}
            healthRecordVaccinations={healthRecord?.vaccinationActs}
            healthRecordId={healthRecord?.id}
            healthRecordName={healthRecord ? `${healthRecord.firstNames} ${healthRecord.lastName}` : undefined}
            healthRecordBirthDate={healthRecord?.birthDate?.toISOString()}
            onImportComplete={refetch}
          />
        )}
      </Paper>
    </>
  );
}
